/**
 * Build-status reducer — folds a live `creations/{id}/versions/{n}` document
 * (the doc the Studio Iterate panel subscribes to) into the view-model the
 * progress UI renders: the phase tracker over @djgg/creations' ONE
 * `BUILD_PHASES` list, a "step n of N" counter, and a plain-language detail
 * line for the current sub-step (round r of R on model M, check k by name,
 * quality pass p of P).
 *
 * PURE (no React, no store) so the mapping from a version doc to UI state is
 * directly unit-testable against fixture snapshots.
 *
 * Two inputs, one truth each: `buildStatus` (the lifecycle state machine —
 * the ONLY truth for queued / live / failed) and `buildProgress` (the
 * builder's live pointer — WHICH phase a running build is in, mirrored from
 * its `progress` trace steps). A doc the worker has not reported on yet falls
 * back to the coarse lifecycle position.
 */
import {
  BUILD_PHASES,
  COMPOSITION_MEDIA_RELEASE_MISSED,
  MEASURED_DEADLINE_EXHAUSTED,
  type BuildPhase,
  type BuildProgress,
  type BuildStatus,
  type CreationVersion,
} from '@djgg/creations';
import { ModelRegistry } from '@djgg/llm';

/** Plain-language phase names (the tracker pills + the counter line). */
export const BUILD_PHASE_LABELS: Record<BuildPhase, string> = {
  queued: 'Getting your workbench ready',
  setup: 'Unpacking the tools',
  writing: 'Bringing your rules to life',
  gates: 'Checking the moving parts',
  review: 'Making sure it’s safe',
  diagnosis: 'Untangling a snag',
  quality: 'Polishing the fun',
  art: 'Dressing up your world',
  audit: 'Taking it for a test drive',
  publishing: 'Getting your game ready',
  live: 'Let’s play!',
};

export const BUILD_PHASE_SKIP_COPY: Record<BuildPhase, string> = {
  queued: 'The worker was available immediately.',
  setup: 'No separate setup pass was reported.',
  writing: 'This run reused existing code instead of writing new rules.',
  gates: 'No separate moving-parts pass was reported.',
  review: 'This run did not need a separate safety review.',
  diagnosis: 'No failed check needed diagnosis.',
  quality: 'The standard checks were enough for this run.',
  art: 'No new artwork had to be joined in this run.',
  audit: 'No separate rendered test drive was required.',
  publishing: 'No separate publication pass was reported.',
  live: 'The run did not reach a playable version.',
};

/** Plain-language names for the checks the builder reports by id (the
 *  `gates/run.ts` emit names). An id this map does not know renders as-is. */
export const BUILD_GATE_LABELS: Record<string, string> = {
  'render-manifest': 'Render manifest',
  'asset-bindings': 'Asset bindings',
  'loadout-mode': 'Loadout mode',
  'render-path': 'Render path',
  'sound-cues': 'Sound cues',
  'media-authority': 'Media authority',
  'required-media': 'Required artwork and animations',
  'mission-completion': 'Mission victory and defeat',
  typecheck: 'Type check',
  tests: 'Unit tests',
  determinism: 'Determinism proof',
  settings: 'Settings match',
  chapters: 'Chapter select',
  adherence: 'Design adherence',
  playability: 'Headless playtest',
  'vite-build': 'Bundle',
  'secret-scan': 'Secret scan',
  'asset-url-bundle': 'Asset URLs in the bundle',
  'rendered-audit': 'Rendered playtest',
};

export const gateLabel = (id: string): string => BUILD_GATE_LABELS[id] ?? id;

const registry = new ModelRegistry();
const modelName = (id: string): string => registry.get(id)?.displayName ?? id;

/** Per-phase display state in the tracker. `skipped` = an earlier phase this
 *  build never entered (diagnosis, quality, art are conditional). */
export type StepState = 'done' | 'active' | 'pending' | 'failed' | 'skipped';

/** One rendered phase in the tracker. */
export interface BuildStep {
  phase: BuildPhase;
  label: string;
  state: StepState;
  /** Creator-facing reason shown only for a genuinely skipped phase. */
  skipExplanation?: string;
}

/** The view-model the Iterate panel renders. */
export interface BuildStatusView {
  /** Current raw lifecycle status. */
  status: BuildStatus;
  /** Whether the build reached a terminal state (live or failed). */
  terminal: boolean;
  failed: boolean;
  live: boolean;
  /** The phase the build is in (or stopped in). */
  phase: BuildPhase;
  /** 1-based position of `phase` in the tracker, and the phase count. */
  step: number;
  stepCount: number;
  /** Plain-language sub-step line ('' when the phase has no finer detail). */
  detail: string;
  /** Epoch ms the current phase/step began (null before the worker reports). */
  since: number | null;
  /** Ordered phase tracker (one entry per BUILD_PHASES). */
  steps: BuildStep[];
  /** Creator-readable reason a failed build stopped ('' unless failed). The
   *  builder's raw notes are repair prompts for the model; they stay in the
   *  version history. */
  failure: string;
}

/** Builder failure notes open with a stable machine prefix (the gate's
 *  failure text, a measured-window refusal). Ordered: first match wins. */
const BUILD_FAILURE_PREFIXES: ReadonlyArray<readonly [prefix: string, text: string]> = [
  [COMPOSITION_MEDIA_RELEASE_MISSED, 'The art and music could not finish inside this build’s time window. Repair builds it again: it keeps the finished art and does not rewrite the game.'],
  // Every composed media refusal of the builder (a failed or unreleased media
  // child, a missing or changed asset) opens with this.
  ['Required composition media', 'The art or music for your game did not finish. Repair builds it again: it keeps the finished art and does not rewrite the game.'],
  [MEASURED_DEADLINE_EXHAUSTED, 'The build’s time window ran out before work could start.'],
  ['CRITICAL PLAYER AUDIT FAILED', 'The automatic playtest found controls or gameplay that did not respond as expected.'],
  ['CRITICAL PLAYER AUDIT UNAVAILABLE', 'The automatic playtest could not collect enough evidence to approve the game.'],
  ['COMPOSITION NATIVE LOOP FAILED', 'The assembled game did not run its game loop cleanly.'],
  ['PLAYABILITY GATE FAILED', 'The automatic playtest could not play the game through.'],
  ['FAIRNESS GATE FAILED', 'The automatic playtest could not win the game, so a player might not be able to either.'],
  ['WINNABILITY GATE FAILED', 'The automatic playtest could not win the game, so a player might not be able to either.'],
  ['RENDERED AUDIT GATE FAILED', 'The on-screen playtest found a problem with what the game shows.'],
];

const GENERIC_GATE_FAILURE = /^([A-Z][A-Z -]*?) GATE FAILED\b/;

/** The creator-readable line for a failed version's builder notes. */
export function buildFailureSummary(
  gates: Pick<NonNullable<CreationVersion['gates']>, 'notes' | 'compositionRuntimeBlock'> | undefined,
): string {
  if (gates?.compositionRuntimeBlock) {
    return 'Building composed games is paused on the platform right now. Your saved work is safe; repair once building reopens.';
  }
  const notes = gates?.notes.trim() ?? '';
  const known = BUILD_FAILURE_PREFIXES.find(([prefix]) => notes.startsWith(prefix));
  if (known) return known[1];
  const gate = GENERIC_GATE_FAILURE.exec(notes)?.[1];
  if (gate) return `An automatic check did not pass (${gate.toLowerCase()}).`;
  return 'The build stopped before it finished.';
}

/** Where the build is: the reported pointer when there is one, else the
 *  lifecycle status' coarse position. */
function phaseOf(status: BuildStatus, progress: BuildProgress | null): BuildPhase {
  if (status === 'live') return 'live';
  if (status === 'queued') return 'queued';
  if (progress) return progress.phase;
  switch (status) {
    case 'publishing':
      return 'publishing';
    case 'gating':
      // The `gating` transition fires once the gates PASS; the asset join and
      // the rendered audit run under it.
      return 'art';
    case 'generating':
      return 'setup';
    default:
      // failed without ever reporting: it never left the queue.
      return 'queued';
  }
}

/** The plain-language sub-step line for a reported pointer. */
function describe(progress: BuildProgress): string {
  const parts: string[] = [];
  if (progress.constructionStage) parts.push(progress.constructionStage === 'core' ? 'Core gameplay' : 'Complete game');
  const round =
    progress.round !== undefined && progress.rounds !== undefined
      ? `round ${progress.round} of ${progress.rounds}`
      : '';
  const pass =
    progress.pass !== undefined && progress.passes !== undefined
      ? `pass ${progress.pass} of ${progress.passes}`
      : '';
  switch (progress.phase) {
    case 'writing':
      if (round) parts.push(round);
      if (progress.activity?.actualModel) parts.push(modelName(progress.activity.actualModel));
      else if (progress.model) parts.push(`Selected: ${modelName(progress.model)}`);
      if (progress.activity?.fallbackReason) parts.push('provider availability fallback');
      if (progress.activity?.providerActivityAt) parts.push('response activity received');
      break;
    case 'gates':
      if (progress.gate !== undefined && progress.gateIndex !== undefined) {
        parts.push(`check ${progress.gateIndex}: ${gateLabel(progress.gate)}`);
      }
      if (round) parts.push(round);
      break;
    case 'review':
    case 'diagnosis':
      if (round) parts.push(round);
      break;
    case 'quality':
      if (pass) parts.push(pass);
      break;
    default:
      break;
  }
  // A targeted quality pass re-runs writing/gates/review: say which pass.
  if (progress.phase !== 'quality' && pass) parts.push(`quality ${pass}`);
  return parts.join(' · ');
}

/**
 * Reduce a version doc into the build-status view-model. Accepts a partial (a
 * fresh snapshot may arrive before every field is populated) and defaults
 * missing fields safely.
 */
export function reduceBuildStatus(
  version: Pick<CreationVersion, 'buildStatus'> & Partial<CreationVersion>,
): BuildStatusView {
  const status = version.buildStatus;
  const progress = version.buildProgress ?? null;
  const failed = status === 'failed';
  const live = status === 'live';
  const phase = phaseOf(status, progress);
  const activeIndex = BUILD_PHASES.indexOf(phase);
  // With a report, an earlier phase is `done` only if the build actually
  // entered it; without one there is nothing to distinguish, so it is done.
  const reached = progress ? new Set<BuildPhase>(['queued', ...(progress.reached ?? [])]) : null;

  const steps: BuildStep[] = BUILD_PHASES.map((p, i) => {
    let state: StepState;
    if (live || i < activeIndex) {
      state = reached && p !== 'live' && !reached.has(p) ? 'skipped' : 'done';
    } else if (i === activeIndex) {
      state = failed ? 'failed' : 'active';
    } else {
      state = 'pending';
    }
    return {
      phase: p,
      label: BUILD_PHASE_LABELS[p],
      state,
      ...(state === 'skipped' ? { skipExplanation: BUILD_PHASE_SKIP_COPY[p] } : {}),
    };
  });

  return {
    status,
    terminal: failed || live,
    failed,
    live,
    phase,
    step: activeIndex + 1,
    stepCount: BUILD_PHASES.length,
    detail: progress && !live ? describe(progress) : '',
    since: progress?.at ?? null,
    steps,
    failure: failed ? buildFailureSummary(version.gates) : '',
  };
}
